// ADR 5.9.26u §3 — the expanded chrome: the rail, drawn with room.
//
// This is **not** a third `navChrome` value. The table still answers `rail`;
// `sizeClass === 'expanded'` only changes how that verdict is drawn, because a
// desk has the horizontal width for a label beside each icon that a tablet in
// portrait does not. The decision stays in the table; the presentation is the
// web's, and adding a `sidebar` row to `window_size_classes.json` would put a
// web-only concept in front of two native clients that have no use for it.
//
// ADR 15.9.26ah §4 — Material 3's navigation drawer: a tonal surface with a
// rounded trailing edge rather than a ruled column, and full-width pill rows.
// `w-64`, not M3's 360dp, because this drawer is *persistent* and shares the
// window with 5.9.26u §4's two-pane grid — at exactly 840dp a 360px drawer
// would squeeze the detail column to 480px; 360 is a *modal* drawer's width.

import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'

import { FOCUS_RING } from '../../ui/Button'
import { IconButton } from '../../ui/controls'
import { Icon } from '../../ui/icons'
import { SoonNavItem } from './SoonNavItem'
import type { Destination } from './destinations'

const SIDEBAR_ROW = 'flex min-h-12 items-center gap-3 rounded-full px-4 text-label-lg'

/**
 * ADR 8.9.26i §4 — the collapse control, and no state of its own.
 *
 * `Sidebar` and `Rail` are siblings chosen one level up, and a component cannot
 * swap itself for its sibling, so `AdaptiveFrame` owns `collapsed` and this
 * file owns only the control. Nothing here may **spell** a size-class string —
 * no `data-state`, no `'expanded'` in a class name or a variant map: this file
 * is outside 6.9.26p AC6's `exempt` pair, and the failure would surface in
 * `analytics.test.tsx` looking unrelated to its cause.
 *
 * The label is `tutor_contract` ("Collapse"). The key is tutor-namespaced and
 * the string is not — a bare verb with no noun — and one wrong-sounding key
 * costs less than two new keys × 31 locales for a control whose accessible name
 * is one word. Its glyph is `close` (15.9.26ah §4): the icon set has no
 * `menu_open`, and a twenty-first path for one control is that set's line to
 * add, not this file's.
 */
export function Sidebar({ items, onCollapse }: { items: Destination[]; onCollapse: () => void }) {
  const { t } = useTranslation()
  return (
    <nav data-testid="nav-sidebar" className="w-64 rounded-e-card border-e border-boundary bg-surface-container">
      <div className="flex justify-end p-2">
        <IconButton icon="close" label={t('tutor_contract')} onClick={onCollapse} />
      </div>
      <ul className="flex flex-col gap-1 p-3">
        {items.map((item) => (
          <li key={item.path}>
            {/* ADR 30.9.26s §3 — a reserved destination, drawn disabled; the
                label grows so the "Soon" badge sits at the row's end. */}
            {item.soon ? (
              <SoonNavItem item={item} className={SIDEBAR_ROW} labelClassName="flex-1" />
            ) : (
              <NavLink
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `${SIDEBAR_ROW} ${FOCUS_RING} motion-safe:transition-colors duration-short ${
                    isActive
                      ? 'bg-secondary-soft font-medium text-on-secondary-soft'
                      : 'text-ink hover:bg-surface-sunken'
                  }`
                }
              >
                <Icon name={item.icon} />
                <span>{t(item.labelKey)}</span>
              </NavLink>
            )}
          </li>
        ))}
      </ul>
    </nav>
  )
}
